import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { openPdfBlob, fmtError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import {
  ChartBar, Lock, QrCode, Download, List,
  SignOut, Sparkle, ChatCircleText, X, Receipt, CheckCircle, Clock, Warning,
  Tag, TrendUp, TrendDown, CaretDown, FileText, ArrowSquareOut, PencilSimple, Trash, Prohibit,
} from "@phosphor-icons/react";
import { CsDashboard, OodelX, dT } from "../components/dashboard/CsDashboard";
import useSiteContent from "../lib/useSiteContent";
import NotificationBell from "../components/NotificationBell";
import { formatDate, formatRangeInclusive, inclusiveEndDate } from "../lib/date";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const NAV = [
  ["/business", "Overview", ChartBar],
  ["/business/oodel-x", "oodel x", Sparkle],
  ["/business/qr", "QR Codes", QrCode],
  ["/business/documents", "Documents", FileText],
  ["/business/invoices", "Invoices", Receipt],
];

function BizContactPMModal({ open, onClose, businessName }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  if (!open) return null;
  const submit = async () => {
    if (!body.trim()) { toast.error("Please write a message before sending."); return; }
    setSending(true);
    try {
      await api.post("/admin/inbox/contact", { subject: subject || `Inquiry · ${businessName || "business"}`, body });
      toast.success("Sent to your PM — they'll be in touch shortly.");
      setSubject(""); setBody(""); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send."); } finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="biz-contact-pm-modal">
      <div className="bg-white rounded-3xl border border-neutral-200 shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-neutral-100">
          <div>
            <div className="overline text-mango flex items-center gap-2"><ChatCircleText size={14} /> CONTACT YOUR PM</div>
            <h2 className="text-xl font-black tracking-tight mt-1">New message</h2>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-black" data-testid="biz-contact-pm-close"><X size={22} /></button>
        </div>
        <div className="p-6 space-y-4">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (optional)" data-testid="biz-contact-pm-subject" className="rounded-xl" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} placeholder="Tell your PM what you need…" data-testid="biz-contact-pm-body" className="w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-black" />
          <p className="text-xs text-neutral-400">Your message lands directly in the PM inbox and your account manager will be emailed.</p>
          <div className="flex justify-end gap-3 pt-1">
            <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
            <Button onClick={submit} disabled={sending} className="rounded-xl bg-mango hover:bg-mango/90" data-testid="biz-contact-pm-send">{sending ? "Sending…" : "Send to PM"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function BusinessLayout() {
  const { user, logout, loading } = useAuth();
  const content = useSiteContent();
  const logoUrl = content.cs_business_dashboard_logo_url || "https://customer-assets-agu9un31.emergentagent.net/job_52b80491-cfc2-49db-abec-517d4bc3bb9d/artifacts/gd2u1wh4_oodelscore.png";
  const nav = useNavigate();
  const [contactOpen, setContactOpen] = useState(false);
  const [overdueInvoice, setOverdueInvoice] = useState(null);
  const [drawer, setDrawer] = useState(false);
  useEffect(() => {
    if (!loading && (!user || !user.is_cs_business)) nav("/login");
  }, [user, loading, nav]);
  useEffect(() => {
    if (user?.billing_suspended) {
      api.get("/business/me/invoices").then((r) => {
        setOverdueInvoice(r.data.find((i) => i.status === "overdue" || i.status === "pending") || null);
      }).catch(() => {});
    }
  }, [user?.billing_suspended]);
  if (!user) return <div className="p-12 text-sm text-neutral-400">Loading…</div>;
  const items = [...NAV, ...(user.tier === "business+" ? [["/business/insights", "Private Insights", Lock]] : [])];
  const onLogout = () => { logout(); nav("/"); };
  const downloadOverduePdf = async () => {
    if (!overdueInvoice) return;
    try {
      const res = await api.get(`/business/me/invoices/${overdueInvoice.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `invoice-${overdueInvoice.id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download invoice PDF"); }
  };

  return (
    <div className="min-h-screen flex bg-[#F4F7F6]" data-testid="business-shell">
      <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-white border-r border-neutral-200 sticky top-0 h-screen" data-testid="business-sidebar">
        <div className="h-24 flex items-center justify-between px-6 border-b border-neutral-100">
          <Link to="/business" data-testid="business-sidebar-logo" className="flex items-center">
            <img src={logoUrl} alt="oodel SCORE" className="w-[180px] h-auto" />
          </Link>
          <NotificationBell />
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {items.map(([to, label, Icon]) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/business"}
              data-testid={`business-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-mango/10 text-mango font-semibold" : "text-neutral-600 hover:bg-neutral-100 hover:text-black"}`}
            >
              <Icon size={18} /> {label === "oodel x" ? <OodelX /> : label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-neutral-100 p-4">
          <div className="text-sm font-semibold truncate mb-0.5">{user.business_name}</div>
          <div className="text-xs text-neutral-400 mb-3 capitalize">{user.tier} tier</div>
          <button onClick={() => setContactOpen(true)} data-testid="business-contact-pm" className="w-full flex items-center justify-center gap-1.5 text-xs font-bold text-mango border border-mango/30 bg-mango/5 hover:bg-mango/10 rounded-xl py-2 mb-3"><ChatCircleText size={14} /> Contact my PM</button>
          <button onClick={onLogout} className="text-xs text-neutral-400 hover:text-red-600 flex items-center gap-1.5" data-testid="business-logout"><SignOut size={14} /> Sign out</button>
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-neutral-200 h-16 flex items-center justify-between px-4">
          <img src={logoUrl} alt="oodel SCORE" data-testid="business-mobile-logo" className="w-[120px] h-auto" />
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Sheet open={drawer} onOpenChange={setDrawer}>
              <SheetTrigger asChild>
                <button data-testid="business-menu-trigger" aria-label="Open menu" className="w-9 h-9 inline-flex items-center justify-center border border-neutral-200 rounded-lg text-neutral-700">
                  <List size={20} weight="bold" />
                </button>
              </SheetTrigger>
            <SheetContent side="left" className="w-[85vw] max-w-xs p-0 flex flex-col" data-testid="business-drawer">
              <SheetHeader className="p-5 border-b border-neutral-100 flex-shrink-0">
                <SheetTitle asChild>
                  <img src={logoUrl} alt="oodel SCORE" className="w-36 h-auto" />
                </SheetTitle>
              </SheetHeader>
              <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
                {items.map(([to, label, Icon]) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === "/business"}
                    onClick={() => setDrawer(false)}
                    data-testid={`business-nav-mobile-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-mango/10 text-mango font-semibold" : "text-neutral-600 hover:bg-neutral-100 hover:text-black"}`}
                  >
                    <Icon size={18} /> {label === "oodel x" ? <OodelX /> : label}
                  </NavLink>
                ))}
              </nav>
              <div className="border-t border-neutral-100 p-4 flex-shrink-0">
                <div className="text-sm font-semibold truncate mb-0.5">{user.business_name}</div>
                <div className="text-xs text-neutral-400 mb-3 capitalize">{user.tier} tier</div>
                <button onClick={() => { setContactOpen(true); setDrawer(false); }} data-testid="business-contact-pm-mobile" className="w-full flex items-center justify-center gap-1.5 text-xs font-bold text-mango border border-mango/30 bg-mango/5 hover:bg-mango/10 rounded-xl py-2 mb-3"><ChatCircleText size={14} /> Contact my PM</button>
                <button onClick={onLogout} className="text-xs text-neutral-400 hover:text-red-600 flex items-center gap-1.5" data-testid="business-logout-mobile"><SignOut size={14} /> Sign out</button>
              </div>
            </SheetContent>
            </Sheet>
          </div>
        </header>
        {user.billing_suspended && (
          <div className="bg-red-600 text-white px-5 md:px-8 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="business-suspended-banner">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Warning size={18} weight="fill" />
              Your account is suspended due to a missed payment — your public QR listing is paused until it's settled.
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {overdueInvoice && <span className="text-sm font-bold">{overdueInvoice.currency} {overdueInvoice.amount?.toLocaleString()} due</span>}
              <Button size="sm" onClick={downloadOverduePdf} disabled={!overdueInvoice} className="rounded-full bg-white text-red-600 hover:bg-red-50 h-8 text-xs font-bold" data-testid="business-suspended-download-invoice">Download &amp; pay invoice</Button>
            </div>
          </div>
        )}
        <main className="flex-1 p-5 md:p-8 max-w-5xl w-full mx-auto">
          <Outlet />
        </main>
      </div>
      <BizContactPMModal open={contactOpen} onClose={() => setContactOpen(false)} businessName={user.business_name} />
    </div>
  );
}

export function BusinessOverview() {
  return (
    <div data-testid="business-overview-page">
      <CsDashboard isAdmin={false} />
    </div>
  );
}

const ASSET_KIT_LABELS = { a3: "A3 Poster", a4: "A4 Poster", a5: "A5 Poster", sticker_window: "Window sticker", sticker_door: "Door sticker", sticker_square: "Counter sticker" };

export function BusinessQrManagement() {
  const [locs, setLocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQrDownload, setShowQrDownload] = useState(true);
  const content = useSiteContent();
  useEffect(() => { api.get("/business/me/locations").then((r) => setLocs(r.data)).catch(() => toast.error("Failed to load locations")).finally(() => setLoading(false)); }, []);
  useEffect(() => { api.get("/business/me").then((r) => setShowQrDownload(r.data?.visible_metrics?.show_qr_download !== false)).catch(() => {}); }, []);
  return (
    <div data-testid="business-qr-page">
      <h1 className="text-2xl font-black tracking-tight mb-2">{dT(content, "cs_dash_qr_page_title", "QR Codes")}</h1>
      <p className="text-sm text-neutral-500 mb-6">{dT(content, "cs_dash_qr_page_subtitle", "Your live QR codes, one per location/branch. Print one and place it where customers can scan it after their visit.")} <strong>Need a new location or QR code?</strong> Use "Contact my PM" in the sidebar — QR codes are set up by oodel to keep your reporting stable.</p>
      {loading && <div className="text-sm text-neutral-400">Loading…</div>}
      <div className="grid md:grid-cols-2 gap-4">
        {locs.map((l) => (
          <div key={l.id} className="bg-white rounded-2xl border border-neutral-200 p-5" data-testid={`bq-location-${l.id}`}>
            <div className="mb-3">
              <div className="font-bold">{l.name}</div>
              <div className="text-xs text-neutral-400">{l.response_count || 0} responses</div>
            </div>
            {showQrDownload && (
              <>
                <img src={l.qr_image_data || l.qr_image_url} alt="QR code" className="w-32 h-32 mx-auto mb-3" data-testid={`bq-qr-image-${l.id}`} />
                <a href={l.qr_image_data || l.qr_image_url} download={`qr-${l.qr_code}.png`} target="_blank" rel="noreferrer" className="w-full">
                  <Button variant="outline" className="w-full rounded-xl text-xs" data-testid={`bq-download-${l.id}`}><Download size={14} className="mr-1.5" /> {dT(content, "cs_dash_qr_download_btn", "Download QR")}</Button>
                </a>
                <div className="text-[11px] text-neutral-400 mt-2 break-all text-center mb-3">{l.rate_url}</div>
              </>
            )}
            {l.assets && Object.keys(l.assets).length > 0 && (
              <div className={showQrDownload ? "border-t border-neutral-100 pt-3 mt-1" : ""}>
                <div className="text-[11px] font-bold uppercase tracking-wide text-neutral-400 mb-2">{dT(content, "cs_dash_brand_assets_title", "Brand Assets")}</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {Object.entries(l.assets).map(([type, url]) => (
                    <a key={type} href={url} download={`${l.qr_code}-${type}.${url.split(".").pop()}`} target="_blank" rel="noreferrer" data-testid={`bq-kit-download-${l.id}-${type}`} className="flex items-center justify-between gap-1.5 text-xs font-semibold border border-neutral-200 rounded-lg px-2.5 py-1.5 hover:bg-neutral-50">
                      {ASSET_KIT_LABELS[type] || type} <Download size={12} />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
        {!loading && locs.length === 0 && <div className="text-sm text-neutral-400 col-span-2">{dT(content, "cs_dash_qr_empty", "No locations yet — contact your PM to set up your first QR code.")}</div>}
      </div>
    </div>
  );
}

const TIER_LABEL = { weekly: "Weekly", monthly: "Monthly", quarterly: "Quarterly", yearly: "Yearly" };

// Weekly is deliberately basic — plain scans/responses/score arithmetic
// PLUS one short AI-written overview once the week has responses (see
// backend); a quiet week auto-approves instantly. Admin gets full CRUD
// (edit/approve/reject/delete) inline; a business viewer sees a plain
// read-only pulse card.
function WeeklyStatCard({ r, isAdmin = false, onChanged = () => {}, onRequestDelete = () => {} }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(r.summary || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/cs/ai-insights/${r.id}`, { summary: draft });
      toast.success("Updated");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || "Failed to update");
    } finally {
      setBusy(false);
    }
  };
  const decide = async (action) => {
    setBusy(true);
    try {
      await api.post(`/admin/cs/ai-insights/${r.id}/${action}`);
      toast.success(action === "approve" ? "Approved — now live on the business's dashboard" : "Rejected");
      onChanged();
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || `Failed to ${action}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-4" data-testid={`box-row-${r.id}`}>
      <div className="flex items-center justify-between mb-2.5 flex-wrap gap-1">
        <div className="flex items-center gap-1.5 text-xs font-bold text-mango" data-testid={`box-period-${r.id}`}>
          <Sparkle size={13} weight="fill" /> {formatRangeInclusive(r.period_start, r.period_end)}
          {r.location_name && <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-100 rounded-full px-2 py-0.5 ml-1" data-testid={`box-branch-${r.id}`}>{r.location_name}</span>}
        </div>
        <div className="flex items-center gap-2">
          {r.status === "pending_review" && <span className="text-[10px] font-bold uppercase tracking-wide text-amber-600 bg-amber-50 rounded-full px-2 py-0.5" data-testid={`box-status-pending-${r.id}`}>Pending review</span>}
          {r.status === "rejected" && <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-400 bg-neutral-100 rounded-full px-2 py-0.5" data-testid={`box-status-rejected-${r.id}`}>Rejected</span>}
        </div>
      </div>
      <div className="flex items-center gap-5 flex-wrap mb-2.5">
        <div><div className="text-xl font-black">{r.responses ?? 0}</div><div className="text-[11px] text-neutral-400 font-semibold">Responses</div></div>
        <div className="flex items-center gap-1.5">
          <div className="text-xl font-black">{r.avg_score != null ? `${r.avg_score}/5` : "—"}</div>
          {r.score_delta != null && r.score_delta !== 0 && (
            <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${r.score_delta > 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid={`box-score-delta-${r.id}`}>
              {r.score_delta > 0 ? <TrendUp size={13} weight="fill" /> : <TrendDown size={13} weight="fill" />} {Math.abs(r.score_delta)}
            </span>
          )}
          <div className="text-[11px] text-neutral-400 font-semibold">Avg score</div>
        </div>
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} className="rounded-none text-sm min-h-[70px]" data-testid={`box-edit-summary-${r.id}`} />
          <div className="flex items-center gap-2">
            <Button size="sm" disabled={busy} onClick={save} className="rounded-none bg-black hover:bg-neutral-800 text-xs h-8" data-testid={`box-save-${r.id}`}>Save</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => { setDraft(r.summary || ""); setEditing(false); }} className="rounded-none text-xs h-8" data-testid={`box-cancel-${r.id}`}>Cancel</Button>
          </div>
        </div>
      ) : (
        r.summary && <p className="text-sm text-neutral-600 leading-relaxed" data-testid={`box-overview-${r.id}`}>{r.summary}</p>
      )}
      {r.watch_flag && (
        <div className="flex items-start gap-1.5 text-xs font-semibold text-red-600 bg-red-50 rounded-lg px-2.5 py-1.5 mt-3" data-testid={`box-watch-flag-${r.id}`}>
          <Warning size={13} weight="fill" className="mt-0.5 shrink-0" /> {r.watch_flag}
        </div>
      )}
      {isAdmin && !editing && (
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-neutral-100">
          {r.status === "pending_review" && (
            <>
              <button onClick={() => decide("approve")} disabled={busy} className="text-xs font-bold text-emerald-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50" data-testid={`box-approve-${r.id}`}><CheckCircle size={13} weight="fill" /> Approve</button>
              <button onClick={() => decide("reject")} disabled={busy} className="text-xs font-bold text-neutral-500 hover:underline inline-flex items-center gap-1 disabled:opacity-50" data-testid={`box-reject-${r.id}`}><Prohibit size={13} /> Reject</button>
            </>
          )}
          <button onClick={() => setEditing(true)} className="text-xs font-bold text-neutral-500 hover:underline inline-flex items-center gap-1 ml-auto" data-testid={`box-edit-${r.id}`}><PencilSimple size={13} /> Edit</button>
          <button onClick={onRequestDelete} className="text-xs font-bold text-red-600 hover:underline inline-flex items-center gap-1" data-testid={`box-delete-${r.id}`}><Trash size={13} /> Delete</button>
        </div>
      )}
    </div>
  );
}

function InsightCard({ r, isAdmin = false, businessId = null, onChanged = () => {}, onRequestDelete = () => {} }) {
  // Deep-link back to the exact same window this report is about — the
  // period_end from the backend is EXCLUSIVE (block cutoff, e.g.
  // "2026-09-01T00:00" means "up through Aug 31"), while the dashboard's
  // date filter treats a bare `until` date as INCLUSIVE of that whole day
  // — so we subtract a day here to keep both sides looking at the exact
  // same responses. Without this off-by-one correction, clicking through
  // would show one extra day's worth of (newer) responses than the
  // report actually covered, reintroducing the very confusion this link
  // exists to remove.
  const link = (() => {
    if (!r.period_start || !r.period_end) return null; // legacy insight docs from before this field existed — skip the link, don't crash the page
    const untilDate = new Date(r.period_end);
    untilDate.setUTCDate(untilDate.getUTCDate() - 1);
    const qs = new URLSearchParams({
      tab: "Responses", since: r.period_start.slice(0, 10), until: untilDate.toISOString().slice(0, 10),
      ...(r.location_id ? { location_id: r.location_id } : {}),
    }).toString();
    return `${isAdmin ? `/admin/customer-sentiment/${businessId}` : "/business"}?${qs}`;
  })();
  const tierLabel = TIER_LABEL[r.granularity] || "Monthly";

  const [editing, setEditing] = useState(false);
  const [draftSummary, setDraftSummary] = useState(r.summary || "");
  const [draftHighlights, setDraftHighlights] = useState((r.highlights || []).map((h) => ({ ...h })));
  const [busy, setBusy] = useState(false);

  const startEdit = () => { setDraftSummary(r.summary || ""); setDraftHighlights((r.highlights || []).map((h) => ({ ...h }))); setEditing(true); };
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/cs/ai-insights/${r.id}`, { summary: draftSummary, highlights: draftHighlights });
      toast.success("Updated");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || "Failed to update");
    } finally {
      setBusy(false);
    }
  };
  const decide = async (action) => {
    setBusy(true);
    try {
      await api.post(`/admin/cs/ai-insights/${r.id}/${action}`);
      toast.success(action === "approve" ? "Approved — now live on the business's dashboard" : "Rejected");
      onChanged();
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || `Failed to ${action}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-mango p-5" data-testid={`box-row-${r.id}`}>
      <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
        <div className="flex items-center gap-1.5 text-xs font-bold text-mango" data-testid={`box-period-${r.id}`}>
          <Sparkle size={13} weight="fill" />
          <span className="uppercase tracking-wide" data-testid={`box-granularity-${r.id}`}>{tierLabel} recap ·&nbsp;</span>
          {formatRangeInclusive(r.period_start, r.period_end)}
        </div>
        <div className="flex items-center gap-2">
          {r.status === "pending_review" && <span className="text-[10px] font-bold uppercase tracking-wide text-amber-600 bg-amber-50 rounded-full px-2 py-0.5" data-testid={`box-status-pending-${r.id}`}>Pending review</span>}
          {r.status === "rejected" && <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-400 bg-neutral-100 rounded-full px-2 py-0.5" data-testid={`box-status-rejected-${r.id}`}>Rejected</span>}
          {isAdmin && <div className="text-xs text-neutral-400">Generated {formatDate(r.generated_at)}</div>}
        </div>
      </div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-1">
        <p className="text-[11px] text-neutral-400">The bigger picture across the {r.granularity === "yearly" ? "year" : r.granularity === "quarterly" ? "quarter" : "month"} that closed {formatDate(inclusiveEndDate(r.period_end))}.</p>
        {link && (
          <Link to={link} className="inline-flex items-center gap-1 text-[11px] font-bold text-mango hover:underline" data-testid={`box-view-responses-${r.id}`}>
            View these exact responses <ArrowSquareOut size={12} weight="bold" />
          </Link>
        )}
      </div>

      {editing ? (
        <div className="space-y-3 mb-4">
          {draftHighlights.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {draftHighlights.map((h, i) => (
                <Input
                  key={i} value={h.label}
                  onChange={(e) => setDraftHighlights((p) => p.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                  className="rounded-none text-xs" data-testid={`box-edit-highlight-${r.id}-${i}`}
                />
              ))}
            </div>
          )}
          <Textarea value={draftSummary} onChange={(e) => setDraftSummary(e.target.value)} className="rounded-none min-h-[120px]" data-testid={`box-edit-summary-${r.id}`} />
          <div className="flex items-center gap-2">
            <Button size="sm" disabled={busy} onClick={save} className="rounded-none bg-black hover:bg-neutral-800 text-xs h-8" data-testid={`box-save-${r.id}`}>Save</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setEditing(false)} className="rounded-none text-xs h-8" data-testid={`box-cancel-${r.id}`}>Cancel</Button>
          </div>
        </div>
      ) : (
        <>
          {r.highlights?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
              {r.highlights.map((h, i) => {
                const tone = h.positive === true ? "up" : h.positive === false ? "down" : "neutral";
                const Icon = tone === "up" ? TrendUp : tone === "down" ? TrendDown : Sparkle;
                const color = tone === "up" ? "#16A34A" : tone === "down" ? "#E11D48" : "#78716C";
                const bg = tone === "up" ? "bg-emerald-50" : tone === "down" ? "bg-rose-50" : "bg-neutral-50";
                return (
                  <div key={i} className={`flex items-start gap-2 text-xs font-medium text-neutral-700 leading-snug rounded-lg p-2.5 ${bg}`} data-testid={`box-highlight-${r.id}-${i}`}>
                    <Icon size={13} weight="fill" className="shrink-0 mt-0.5" style={{ color }} />
                    <span>{h.label}</span>
                  </div>
                );
              })}
            </div>
          )}

          {r.summary && <p className="text-sm text-neutral-700 leading-relaxed mb-3 whitespace-pre-line" data-testid={`box-narrative-${r.id}`}>{r.summary}</p>}
        </>
      )}

      {r.per_question_demographic_skew?.length > 0 && (
        <div className="mb-3 space-y-1.5 bg-neutral-50 rounded-lg p-3" data-testid={`box-demo-skew-${r.id}`}>
          <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Who's saying what</div>
          {r.per_question_demographic_skew.map((d, i) => (
            <div key={i} className="flex items-start gap-1.5 text-xs text-neutral-600 leading-snug" data-testid={`box-demo-skew-row-${r.id}-${i}`}>
              {d.diff > 0 ? <TrendUp size={12} weight="fill" className="shrink-0 mt-0.5 text-emerald-500" /> : <TrendDown size={12} weight="fill" className="shrink-0 mt-0.5 text-rose-500" />}
              <span><strong>{d.segment}</strong> rate "{d.question}" {d.diff > 0 ? "higher" : "lower"} than average ({d.avg} vs {d.question_overall}, n={d.n})</span>
            </div>
          ))}
        </div>
      )}

      {r.branch_ranking?.length > 0 && (isAdmin || r.show_charts !== false) && (
        <div className="mb-3 bg-neutral-50 rounded-lg p-3" data-testid={`box-branch-ranking-${r.id}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">Branch performance this {r.granularity === "yearly" ? "year" : r.granularity === "quarterly" ? "quarter" : "month"}</div>
            {isAdmin && (
              <label className="flex items-center gap-2 text-xs font-semibold text-neutral-500 cursor-pointer">
                Show chart to business
                <Switch
                  checked={r.show_charts !== false}
                  onCheckedChange={async (v) => { try { await api.put(`/admin/cs/ai-insights/${r.id}`, { show_charts: v }); onChanged(); } catch { toast.error("Failed to update"); } }}
                  data-testid={`box-show-charts-${r.id}`}
                />
              </label>
            )}
          </div>
          <div style={{ width: "100%", height: Math.max(90, r.branch_ranking.length * 30) }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={r.branch_ranking.map((b) => ({ name: b.location_name, avg: b.avg_score }))} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 4 }}>
                <XAxis type="number" domain={[0, 5]} hide />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: "#525252" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="avg" name="Avg score" fill="#F5A524" radius={[0, 4, 4, 0]} maxBarSize={16} label={{ position: "right", fontSize: 11, formatter: (v) => `${v}/5` }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {r.watch_flags?.length > 0 && (
        <div className="mb-3 space-y-1">
          {r.watch_flags.map((f, i) => (
            <div key={i} className="flex items-start gap-1.5 text-xs font-semibold text-red-600 bg-red-50 rounded-lg px-2.5 py-1.5" data-testid={`box-watch-flag-${r.id}-${i}`}>
              <Warning size={13} weight="fill" className="mt-0.5 shrink-0" /> {f}
            </div>
          ))}
        </div>
      )}

      {r.top_tags?.length > 0 && (
        <div className="pt-3 border-t border-neutral-100" data-testid={`box-top-tags-${r.id}`}>
          <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1.5">Top comment topics</div>
          <div className="flex flex-wrap gap-1.5">
            {r.top_tags.map((t, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-xs bg-neutral-100 text-neutral-600 rounded-full px-2 py-0.5"><Tag size={10} /> {t.tag} ({t.count})</span>
            ))}
          </div>
        </div>
      )}

      {isAdmin && !editing && (
        <div className="flex items-center gap-3 mt-4 pt-3 border-t border-neutral-100">
          {r.status === "pending_review" && (
            <>
              <button onClick={() => decide("approve")} disabled={busy} className="text-xs font-bold text-emerald-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50" data-testid={`box-approve-${r.id}`}><CheckCircle size={13} weight="fill" /> Approve</button>
              <button onClick={() => decide("reject")} disabled={busy} className="text-xs font-bold text-neutral-500 hover:underline inline-flex items-center gap-1 disabled:opacity-50" data-testid={`box-reject-${r.id}`}><Prohibit size={13} /> Reject</button>
            </>
          )}
          <button onClick={startEdit} className="text-xs font-bold text-neutral-500 hover:underline inline-flex items-center gap-1 ml-auto" data-testid={`box-edit-${r.id}`}><PencilSimple size={13} /> Edit</button>
          <button onClick={onRequestDelete} className="text-xs font-bold text-red-600 hover:underline inline-flex items-center gap-1" data-testid={`box-delete-${r.id}`}><Trash size={13} /> Delete</button>
        </div>
      )}
    </div>
  );
}

// A flat, already-sorted (newest first, from the backend) list of ONE
// granularity at a time — the business picks Weekly/Monthly/Quarterly/
// Yearly via a 4-way toggle up in `BusinessOodelX`, so there's no mixing
// to do here. Weekly renders as a compact stat card; the other three as
// the full narrative card. `isAdmin` unlocks full CRUD (edit/approve/
// reject/delete) inline on every card, wherever this feed is mounted —
// not just from the separate global Review Queue page.
export function AiInsightsFeed({ rows, isAdmin = false, businessId = null, onChanged = () => {} }) {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  if (!rows) return null;

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/cs/ai-insights/${deleteTarget.id}`);
      toast.success("Deleted — removed from the business's dashboard.");
      setDeleteTarget(null);
      onChanged();
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="ai-insights-timeline">
      {rows.map((r) => r.granularity === "weekly"
        ? <WeeklyStatCard key={r.id} r={r} isAdmin={isAdmin} onChanged={onChanged} onRequestDelete={() => setDeleteTarget(r)} />
        : <InsightCard key={r.id} r={r} isAdmin={isAdmin} businessId={businessId} onChanged={onChanged} onRequestDelete={() => setDeleteTarget(r)} />
      )}

      {isAdmin && (
        <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <DialogContent className="rounded-none" data-testid="ai-insight-delete-dialog">
            <DialogHeader><DialogTitle>Delete this insight permanently?</DialogTitle></DialogHeader>
            <p className="text-sm text-neutral-500">
              This removes the {formatRangeInclusive(deleteTarget?.period_start, deleteTarget?.period_end)} report
              entirely — it disappears from the business's dashboard immediately (if it was already approved), and
              this cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setDeleteTarget(null)} data-testid="ai-insight-delete-cancel">Cancel</Button>
              <Button className="rounded-none bg-red-600 hover:bg-red-700" disabled={deleting} onClick={confirmDelete} data-testid="ai-insight-delete-confirm">
                {deleting ? "Deleting…" : "Delete permanently"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Bars = Scans (top-of-funnel traffic), line = Responses (actual
// conversion) — chronological left-to-right, oldest period first, so the
// eye reads it like a normal trend line rather than backwards. `rows` is
// whatever one-granularity feed the caller already fetched (weekly/
// monthly/quarterly/yearly), each of which carries plain `scans` /
// `responses` counts regardless of tier.
function oodelXPeriodLabel(r) {
  const d = new Date(r.period_start);
  if (r.granularity === "yearly") return `${d.getUTCFullYear()}`;
  if (r.granularity === "quarterly") return `Q${Math.floor(d.getUTCMonth() / 3) + 1} '${String(d.getUTCFullYear()).slice(2)}`;
  if (r.granularity === "monthly") return d.toLocaleDateString("en-US", { month: "short", year: "2-digit", timeZone: "UTC" });
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

export function ScansResponsesChart({ rows }) {
  if (!rows || rows.length === 0) return null;
  const data = [...rows].reverse().map((r) => ({
    label: oodelXPeriodLabel(r), scans: r.scans ?? 0, responses: r.responses ?? 0,
  }));
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-4 mb-4" data-testid="oodelx-scans-responses-chart">
      <div className="text-xs font-bold text-neutral-500 uppercase tracking-wide mb-3">Scans vs. Responses</div>
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F0F0F0" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#A3A3A3" }} axisLine={{ stroke: "#E5E5E5" }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#A3A3A3" }} allowDecimals={false} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
            <Bar dataKey="scans" name="Scans" fill="#BBF7D0" radius={[4, 4, 0, 0]} maxBarSize={36} />
            <Line type="monotone" dataKey="responses" name="Responses" stroke="#16A34A" strokeWidth={2.5} dot={{ r: 3, fill: "#16A34A" }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const OODELX_TIERS = ["weekly", "monthly", "quarterly", "yearly"];

export function BusinessOodelX() {
  const [rows, setRows] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState(null);
  const [granularity, setGranularity] = useState("weekly");
  const content = useSiteContent();
  useEffect(() => { api.get("/business/me/locations").then((r) => setLocations(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    // Monthly/Quarterly/Yearly are combined whole-business reports only —
    // picking a specific branch snaps back to Weekly, the one tier that's
    // actually generated per branch.
    if (locationId && granularity !== "weekly") setGranularity("weekly");
  }, [locationId, granularity]);
  useEffect(() => {
    setRows(null);
    const params = { granularity, ...(locationId ? { location_id: locationId } : {}) };
    api.get("/business/me/ai-insights", { params }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [locationId, granularity]);
  const allBranchesLabel = dT(content, "cs_dash_branch_all_label", "All branches");
  // Weekly + "All branches" mixes every branch's own reports together — a
  // trend chart plotting them all on the same period label would just be
  // visual noise, so it's shown only once there's one consistent series.
  const showTrendChart = !(granularity === "weekly" && !locationId && locations.length > 1);
  return (
    <div data-testid="business-oodel-x-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
        <h1 className="text-2xl font-black tracking-tight"><OodelX /> insights</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center border border-neutral-200 rounded-lg overflow-hidden text-xs font-semibold" data-testid="oodelx-granularity-toggle">
            {OODELX_TIERS.map((g) => {
              const disabled = g !== "weekly" && !!locationId;
              return (
                <button
                  key={g} disabled={disabled} onClick={() => setGranularity(g)}
                  className={`px-3 py-2 capitalize ${disabled ? "bg-neutral-50 text-neutral-300 cursor-not-allowed" : granularity === g ? "bg-black text-white" : "bg-white text-neutral-600 hover:bg-neutral-50"}`}
                  data-testid={`oodelx-granularity-${g}`}
                  title={disabled ? `${g.charAt(0).toUpperCase() + g.slice(1)} is one combined report for every branch — pick "${allBranchesLabel}" to see it` : ""}
                >
                  {g}
                </button>
              );
            })}
          </div>
          {locations.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 text-xs font-semibold text-neutral-600 bg-white border border-neutral-200 rounded-lg px-3 py-2 hover:bg-neutral-50" data-testid="oodelx-branch-trigger">
                  {locationId ? (locations.find((l) => l.id === locationId)?.name || "Branch") : allBranchesLabel} <CaretDown size={11} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLocationId(null)} data-testid="oodelx-branch-all" className={!locationId ? "font-bold" : ""}>
                  {!locationId ? "✓ " : ""}{allBranchesLabel}
                </DropdownMenuItem>
                {locations.map((l) => (
                  <DropdownMenuItem key={l.id} onClick={() => setLocationId(l.id)} data-testid={`oodelx-branch-${l.id}`} className={locationId === l.id ? "font-bold" : ""}>
                    {locationId === l.id ? "✓ " : ""}{l.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      <p className="text-sm text-neutral-500 mb-6">{dT(content, "cs_dash_oodel_x_subtitle", "Weekly numbers, monthly stories, quarterly and yearly recaps — all generated automatically, calendar-aligned, no action needed from you.")}</p>
      {rows === null && <div className="text-sm text-neutral-400">Loading…</div>}
      {showTrendChart && <ScansResponsesChart rows={rows} />}
      <AiInsightsFeed rows={rows} />

      {rows && rows.length === 0 && <div className="text-sm text-neutral-400">{dT(content, "cs_dash_oodel_x_empty", "No insights yet for this period — check back once you've collected a bit more feedback.")}</div>}
    </div>
  );
}

const INVOICE_STATUS_STYLE = {
  paid: { icon: CheckCircle, label: "Paid", cls: "text-emerald-600 bg-emerald-50" },
  pending: { icon: Clock, label: "Pending", cls: "text-amber-600 bg-amber-50" },
  overdue: { icon: Warning, label: "Overdue", cls: "text-red-600 bg-red-50" },
};

export function BusinessInvoices() {
  const [rows, setRows] = useState(null);
  const [viewUrl, setViewUrl] = useState(null);
  const content = useSiteContent();
  useEffect(() => { api.get("/business/me/invoices").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  const fmtDate = (d) => formatDate(d);
  const invoiceNo = (id) => `INV-${id.slice(0, 8).toUpperCase()}`;
  const downloadPdf = async (iid) => {
    try {
      const res = await api.get(`/business/me/invoices/${iid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `invoice-${iid.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download invoice PDF"); }
  };
  // Renders the PDF in an in-page modal (below) rather than a new browser
  // tab/window — a new-tab approach hit two dead ends: Chrome blocks
  // top-level navigation to a data: URL, and a blob: URL is scoped to the
  // browsing context that created it so it can't be handed to a *different*
  // window. Staying in the same window/document sidesteps both entirely.
  const viewPdf = async (iid) => {
    try {
      const res = await api.get(`/business/me/invoices/${iid}/pdf`, { responseType: "blob" });
      setViewUrl(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })));
    } catch { toast.error("Failed to open invoice PDF"); }
  };
  const closeView = () => { if (viewUrl) URL.revokeObjectURL(viewUrl); setViewUrl(null); };
  return (
    <div data-testid="business-invoices-page">
      <h1 className="text-2xl font-black tracking-tight mb-2">{dT(content, "cs_dash_invoices_title", "Invoices")}</h1>
      <p className="text-sm text-neutral-500 mb-6">{dT(content, "cs_dash_invoices_subtitle", "Invoices are generated automatically on your agreed billing cycle. Once you've paid, your account manager marks it here as Paid.")}</p>
      {rows === null && <div className="text-sm text-neutral-400">Loading…</div>}
      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 text-xs text-neutral-400 text-left">
              <th className="py-3 px-5 font-medium">Invoice #</th>
              <th className="py-3 px-5 font-medium">Invoice Date</th>
              <th className="py-3 px-5 font-medium">Period</th>
              <th className="py-3 px-5 font-medium">Cycle</th>
              <th className="py-3 px-5 font-medium text-right">Amount</th>
              <th className="py-3 px-5 font-medium text-right">Status</th>
              <th className="py-3 px-5 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows && rows.map((r) => {
              const st = INVOICE_STATUS_STYLE[r.status] || INVOICE_STATUS_STYLE.pending;
              const Icon = st.icon;
              return (
                <tr key={r.id} className="border-b border-neutral-100" data-testid={`biz-invoice-row-${r.id}`}>
                  <td className="py-3 px-5 font-mono text-xs text-neutral-600" data-testid={`biz-invoice-number-${r.id}`}>{invoiceNo(r.id)}</td>
                  <td className="py-3 px-5 text-neutral-500" data-testid={`biz-invoice-date-${r.id}`}>{fmtDate(r.created_at)}</td>
                  <td className="py-3 px-5">{fmtDate(r.period_start)} – {fmtDate(r.period_end)}</td>
                  <td className="py-3 px-5 capitalize text-neutral-500">{r.billing_cycle}</td>
                  <td className="py-3 px-5 text-right font-bold">{r.currency} {r.amount?.toLocaleString()}</td>
                  <td className="py-3 px-5 text-right">
                    <span className={`inline-flex items-center gap-1 text-xs font-bold rounded-full px-2.5 py-1 ${st.cls}`} data-testid={`biz-invoice-status-${r.id}`}><Icon size={12} weight="fill" /> {st.label}</span>
                  </td>
                  <td className="py-3 px-5 text-right whitespace-nowrap">
                    <button onClick={() => viewPdf(r.id)} data-testid={`biz-invoice-view-${r.id}`} className="text-xs underline text-neutral-500 hover:text-black mr-3">View</button>
                    <button onClick={() => downloadPdf(r.id)} data-testid={`biz-invoice-pdf-${r.id}`} className="text-xs underline text-neutral-500 hover:text-black">Download</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows && rows.length === 0 && <div className="text-sm text-neutral-400 p-5">No invoices yet.</div>}
      </div>
      {viewUrl && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={closeView} data-testid="biz-invoice-view-modal">
          <div className="bg-white rounded-xl w-full max-w-3xl h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2 border-b border-neutral-200">
              <span className="text-sm font-bold">Invoice</span>
              <button onClick={closeView} data-testid="biz-invoice-view-close" className="text-sm text-neutral-500 hover:text-black">Close ✕</button>
            </div>
            <iframe src={viewUrl} title="Invoice PDF" className="flex-1 w-full border-none" />
          </div>
        </div>
      )}
    </div>
  );
}

export function BusinessPrivateInsights() {
  const [rows, setRows] = useState(null);
  const content = useSiteContent();
  useEffect(() => { api.get("/business/me/private-insights").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (rows === null) return <div className="text-sm text-neutral-400">Loading…</div>;
  return (
    <div data-testid="business-insights-page">
      <h1 className="text-2xl font-black tracking-tight mb-2">{dT(content, "cs_dash_private_insights_title", "Private Insights")}</h1>
      <p className="text-sm text-neutral-500 mb-6">{dT(content, "cs_dash_private_insights_subtitle", "Commissioned studies and deep-dive reports prepared for you by the oodel. research team.")}</p>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl border border-neutral-200 p-5" data-testid={`bpi-row-${r.id}`}>
            <div className="font-bold mb-1">{r.title}</div>
            {r.description && <p className="text-sm text-neutral-500 mb-2">{r.description}</p>}
            <div className="text-xs text-neutral-400 mb-2">{formatDate(r.created_at)}</div>
            {r.report_url && <a href={r.report_url} target="_blank" rel="noreferrer" className="text-sm font-bold text-mango hover:underline">View report →</a>}
          </div>
        ))}
        {rows.length === 0 && <div className="text-sm text-neutral-400">{dT(content, "cs_dash_private_insights_empty", "No private studies yet. Your account manager will add them here as they're delivered.")}</div>}
      </div>
    </div>
  );
}

const DOC_STATUS_META = {
  draft: { label: "Draft", cls: "text-neutral-500 bg-neutral-100" },
  pending_signatures: { label: "Awaiting signature", cls: "text-amber-700 bg-amber-50" },
  completed: { label: "Fully signed", cls: "text-emerald-700 bg-emerald-50" },
};

export function BusinessDocuments() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/business/me/documents").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (rows === null) return <div className="text-sm text-neutral-400">Loading…</div>;
  return (
    <div data-testid="business-documents-page">
      <h1 className="text-2xl font-black tracking-tight mb-2">Documents</h1>
      <p className="text-sm text-neutral-500 mb-6">Pilot proposals, commercial agreements, and any other document exchanged with oodel — with a full signing history.</p>
      <div className="space-y-3">
        {rows.map((d) => {
          const meta = DOC_STATUS_META[d.status];
          return (
            <div key={d.id} className="bg-white rounded-2xl border border-neutral-200 p-5" data-testid={`biz-doc-row-${d.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="font-bold">{d.title}</div>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
              </div>
              <div className="text-xs text-neutral-400 mb-3">{d.doc_type} · Created {formatDate(d.created_at)}</div>
              <button onClick={() => openPdfBlob(`/business/me/documents/${d.id}/pdf`, `${d.title}.pdf`).catch(() => toast.error("Couldn't open this document."))} className="text-sm font-bold text-mango hover:underline" data-testid={`biz-doc-view-${d.id}`}>View / download PDF →</button>
            </div>
          );
        })}
        {rows.length === 0 && <div className="text-sm text-neutral-400">No documents yet.</div>}
      </div>
    </div>
  );
}
